import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Flex, Form, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import dayjs from "dayjs";

import { UserForm } from "components";
import { NOTIFICATION_TYPE_ENUM, UserCreateRequest } from "interfaces";
import { useStores } from "stores";
import { editUser, getStatusMessage } from "services";
import { routes } from "constants/routes";
import { dateISOFormat, getPhoneMaskValue, isValidDateRange, parseErrorMessage } from "utils";
import { useNotificationContext } from "providers";

export const UserEditPage = observer(() => {
  const { userCard } = useStores();
  const { id } = useParams();
  const navigate = useNavigate();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (id) {
      userCard.fetchCardById(id);
    }
  }, [id, userCard]);

  const closeResultModal = () => {
    return Promise.resolve(navigate(`/${routes.users}/${userCard.card.id}`));
  };

  const saveUser = async () => {
    setLoading(true);
    const values = form.getFieldsValue();

    const dataToSend: UserCreateRequest = {
      ...values,
      phone: getPhoneMaskValue(values.phone),
      attorneyDate: isValidDateRange(values.attorneyDate) ? dateISOFormat(values.attorneyDate[0], false) : "",
      attorneyExpirationDate: isValidDateRange(values.attorneyDate) ? dateISOFormat(values.attorneyDate[1], false) : "",
    };

    await editUser({ id: userCard.card.id }, dataToSend)
      .then(() => {
        openNotification({
          message: "Отправлено успешно",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setTimeout(() => {
          closeResultModal();
          setLoading(false);
        }, 1500);
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        setLoading(false);
      });
  };

  const initValues = {
    ...userCard.card,
    attorney: userCard.card.attorney ?? "",
    attorneyDate: [
      userCard.card.attorneyDate ? dayjs(userCard.card.attorneyDate) : undefined,
      userCard.card.attorneyExpirationDate ? dayjs(userCard.card.attorneyExpirationDate) : undefined,
    ],
  } as any;

  return (
    <Spin spinning={userCard.isPending}>
      {userCard.fetchStatus === "success" && (
        <Flex vertical>
          <Typography.Title>Редактировать данные пользователя</Typography.Title>
          <UserForm
            isEditPage
            initValues={initValues}
            form={form}
            disabled={loading}
            closeHandler={closeResultModal}
            onFinish={saveUser}
          />
        </Flex>
      )}
      {userCard.fetchStatus === "error" && userCard.fetchError && (
        <Alert
          message={getStatusMessage(userCard.fetchError.status)}
          description={parseErrorMessage(userCard.fetchError)}
          type="error"
          showIcon
        />
      )}
    </Spin>
  );
});
